import { useCallback, useState } from "react";
import { toast } from "sonner";
import { previewArchitecturalPdf, requestArchitecturalPdf, requestIfcExport } from "../../lib/api";

/**
 * Architectural PDF + IFC export lightbox state machine for the
 * FloorPlan2D editor.
 *
 * State: "idle" | "previewing" | "downloading" | "paywall"
 * Returns the state, the data-URL of the most recent preview, and three
 * action handlers (preview, downloadPDF, downloadIFC) plus a close helper.
 *
 * Extracted from FloorPlan2D.jsx (V2.0.18) so the export flow is
 * isolated from the canvas rendering code.
 *
 * V2.0.80 — handlers wrapped in useCallback so the memoized
 * `ExportLightbox` component actually skips re-renders when unrelated
 * parent state changes. Without stable callback refs, `React.memo` on
 * the lightbox is a no-op.
 */
export default function useExportActions(designId) {
  const [pdfState, setPdfState] = useState("idle");
  const [previewDataUrl, setPreviewDataUrl] = useState(null);

  const handlePreviewPDF = useCallback(async () => {
    if (!designId) return;
    setPdfState("previewing");
    try {
      const r = await previewArchitecturalPdf(designId);
      const { pdf_base64 } = r.data || {};
      if (!pdf_base64) throw new Error("No PDF in response");
      setPreviewDataUrl(`data:application/pdf;base64,${pdf_base64}`);
    } catch (_e) {
      toast.error("Could not load preview. Try again.");
      setPdfState("idle");
    }
  }, [designId]);

  const handleDownloadCleanPDF = useCallback(async () => {
    if (!designId) return;
    setPdfState("downloading");
    try {
      const r = await requestArchitecturalPdf(designId);
      const { pdf_base64, filename } = r.data || {};
      if (!pdf_base64) throw new Error("No PDF in response");
      const blob = await fetch(`data:application/pdf;base64,${pdf_base64}`).then((x) => x.blob());
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "floor_plan.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Architectural floor plan downloaded.");
      setPdfState("idle");
      setPreviewDataUrl(null);
    } catch (e) {
      if (e?.response?.status === 402) {
        setPdfState("paywall");
      } else {
        toast.error("Could not generate PDF. Try again.");
        setPdfState("previewing");
      }
    }
  }, [designId]);

  const handleDownloadIFC = useCallback(async () => {
    if (!designId) return;
    setPdfState("downloading");
    try {
      const r = await requestIfcExport(designId);
      const { ifc_base64, filename } = r.data || {};
      if (!ifc_base64) throw new Error("No IFC in response");
      const blob = await fetch(`data:application/octet-stream;base64,${ifc_base64}`).then((x) => x.blob());
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "design.ifc";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("IFC file downloaded — opens in Revit, ArchiCAD & every major BIM tool.");
      setPdfState("idle");
      setPreviewDataUrl(null);
    } catch (e) {
      if (e?.response?.status === 402) {
        setPdfState("paywall");
      } else {
        toast.error("Could not generate IFC. Try again.");
        setPdfState("previewing");
      }
    }
  }, [designId]);

  const closeLightbox = useCallback(() => {
    setPdfState("idle");
    setPreviewDataUrl(null);
  }, []);

  return {
    pdfState,
    setPdfState,
    previewDataUrl,
    handlePreviewPDF,
    handleDownloadCleanPDF,
    handleDownloadIFC,
    closeLightbox,
  };
}
